import React, { useState, useEffect } from 'react';
import { fetchLogsWithFilters, fetchAccounts, fetchAssets } from '../../services/api';
import theme from '../../theme';
import { LOGS_VIEWER_TIMEFRAMES } from './logsViewerConstants';

const LogsViewer = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [symbols, setSymbols] = useState([]);

  const staticFilterOptions = {
    levels: ['system', 'main', 'scanner', 'debug', 'snapshot'],
    brokers: ['ib', 'okx'],
    timeframes: LOGS_VIEWER_TIMEFRAMES
  };

  const getUserTimezone = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  };

  const getLocalTimeRange = () => {
    const now = new Date();
    const future = new Date(now.getTime() + 10 * 60 * 1000);
    const past = new Date(now.getTime() - 24 * 60 * 60 * 1000);

    const formatForInput = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    return {
      time_from: formatForInput(past),
      time_to: formatForInput(future)
    };
  };

  const loadFiltersFromStorage = () => {
    try {
      const saved = localStorage.getItem('trading-logs-filters');
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          ...parsed,
          ...getLocalTimeRange(),
        };
      }
    } catch (error) {
      console.warn('Failed to load saved filters:', error);
    }

    return {
      levels: ['main', 'system', 'snapshot'],
      brokers: [],
      accounts: [],
      instruments: [],
      timeframes: [],
      ...getLocalTimeRange(),
      size: 100,
      sort_order: 'desc'
    };
  };

  const [filters, setFilters] = useState(loadFiltersFromStorage());

  const [showFilters, setShowFilters] = useState(false);

  const saveFiltersToStorage = (newFilters) => {
    try {
      const { time_from, time_to, ...filtersToSave } = newFilters;
      localStorage.setItem('trading-logs-filters', JSON.stringify(filtersToSave));
    } catch (error) {
      console.warn('Failed to save filters:', error);
    }
  };

  useEffect(() => {
    const loadDynamicFilterOptions = async () => {
      try {
        const accountsData = await fetchAccounts();
        const userAccounts = accountsData
          .filter(acc => !acc.account.startsWith('virtual_') && acc.broker !== 'okx')
          .map(acc => acc.account);
        setAccounts(userAccounts);

        const symbolsSet = new Set();

        for (const accountInfo of accountsData) {
          if (!accountInfo.account.startsWith('virtual_')) {
            try {
              const assets = await fetchAssets(accountInfo.account, accountInfo.broker);
              if (assets && Array.isArray(assets)) {
                assets.forEach(asset => {
                  if (asset.symbol) {
                    symbolsSet.add(asset.symbol);
                  }
                });
              } else if (assets && assets.symbol) {
                symbolsSet.add(assets.symbol);
              }
            } catch (error) {
              console.error(`Error loading assets for ${accountInfo.account}:`, error);
            }
          }
        }

        setSymbols(Array.from(symbolsSet).sort());
      } catch (error) {
        console.error('Error loading dynamic filter options:', error);
      }
    };

    loadDynamicFilterOptions();
    loadLogs();
  }, []);

  const loadLogs = async () => {
    setLoading(true);
    try {
      let adjustedFilters = { ...filters };

      if (adjustedFilters.time_from) {
        const localFrom = new Date(adjustedFilters.time_from);
        adjustedFilters.time_from = localFrom.toISOString();
      }
      if (adjustedFilters.time_to) {
        const localTo = new Date(adjustedFilters.time_to);
        adjustedFilters.time_to = localTo.toISOString();
      }

      const result = await fetchLogsWithFilters(adjustedFilters);
      setLogs(result.logs || []);
    } catch (error) {
      console.error('Error loading logs:', error);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => {
      const newFilters = {
        ...prev,
        [filterType]: value
      };
      saveFiltersToStorage(newFilters);
      return newFilters;
    });
  };

  const handleMultiSelectChange = (filterType, value, checked) => {
    setFilters(prev => {
      const newFilters = {
        ...prev,
        [filterType]: checked
          ? [...prev[filterType], value]
          : prev[filterType].filter(v => v !== value)
      };
      saveFiltersToStorage(newFilters);
      return newFilters;
    });
  };

  const handleApplyFilters = () => {
    loadLogs();
    setShowFilters(false);
  };

  const clearFilters = () => {
    const newFilters = {
      levels: ['main', 'system', 'snapshot'],
      brokers: [],
      accounts: [],
      instruments: [],
      timeframes: [],
      ...getLocalTimeRange(),
      size: 100,
      sort_order: 'desc'
    };
    setFilters(newFilters);
    saveFiltersToStorage(newFilters);
  };

  const setTimeRange = (hours) => {
    const now = new Date();
    const future = new Date(now.getTime() + 10 * 60 * 1000);
    const from = new Date(now.getTime() - hours * 60 * 60 * 1000);

    const formatForInput = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    setFilters(prev => {
      const newFilters = {
        ...prev,
        time_from: formatForInput(from),
        time_to: formatForInput(future)
      };
      saveFiltersToStorage(newFilters);
      return newFilters;
    });
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.levels.length > 0) count++;
    if (filters.brokers.length > 0) count++;
    if (filters.accounts.length > 0) count++;
    if (filters.instruments.length > 0) count++;
    if (filters.timeframes.length > 0) count++;
    return count;
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const userTimezone = getUserTimezone();

    return date.toLocaleString('en-US', {
      timeZone: userTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getLevelBadgeClass = (level) => {
    const classes = {
      'system': 'bg-olive-100 text-olive-700',
      'main': 'bg-emerald-50 text-emerald-700',
      'scanner': 'bg-amber-50 text-amber-700',
      'debug': 'bg-red-50 text-red-700',
      'snapshot': 'bg-olive-200 text-olive-800'
    };
    return classes[level] || 'bg-olive-50 text-olive-600';
  };

  const getBrokerBadgeClass = (broker) => {
    const classes = {
      'ib': 'bg-olive-100 text-olive-700',
      'okx': 'bg-orange-50 text-orange-700'
    };
    return classes[broker] || 'bg-olive-50 text-olive-600';
  };

  return (
    <div className={`${theme.cardBg} ${theme.cardShadow} ${theme.cardRound} p-6`}>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className={`text-xl font-bold ${theme.textPrimary}`}>Trading Logs</h2>
          {getActiveFiltersCount() > 0 && (
            <p className={`text-sm ${theme.textSecondary} mt-1`}>
              {getActiveFiltersCount()} filter(s) active • {logs.length} results
            </p>
          )}
        </div>
        <div className="space-x-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 ${showFilters ? theme.btnPrimary : theme.btnSecondary
              }`}
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
            {getActiveFiltersCount() > 0 && (
              <span className="ml-1 bg-olive-200 text-olive-800 text-xs px-1.5 py-0.5 rounded-full">
                {getActiveFiltersCount()}
              </span>
            )}
          </button>
          <button
            onClick={loadLogs}
            disabled={loading}
            className={`px-4 py-2 rounded-xl text-sm font-semibold ${theme.btnPrimary} disabled:opacity-50`}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {showFilters && (
        <div className={`mb-6 p-4 ${theme.cardBorder} rounded-xl ${theme.sectionBg}`}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

            <div>
              <label className={`block text-sm font-medium ${theme.textSecondary} mb-2`}>Log Levels</label>
              <div className="space-y-1">
                {staticFilterOptions.levels.map(level => (
                  <label key={level} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.levels.includes(level)}
                      onChange={(e) => handleMultiSelectChange('levels', level, e.target.checked)}
                      className="mr-2 accent-olive-600"
                    />
                    <span className={`text-sm capitalize ${theme.textPrimary}`}>{level}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium ${theme.textSecondary} mb-2`}>Brokers</label>
              <div className="space-y-1">
                {staticFilterOptions.brokers.map(broker => (
                  <label key={broker} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.brokers.includes(broker)}
                      onChange={(e) => handleMultiSelectChange('brokers', broker, e.target.checked)}
                      className="mr-2 accent-olive-600"
                    />
                    <span className={`text-sm uppercase ${theme.textPrimary}`}>{broker}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium ${theme.textSecondary} mb-2`}>Accounts</label>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {accounts.map(account => (
                  <label key={account} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.accounts.includes(account)}
                      onChange={(e) => handleMultiSelectChange('accounts', account, e.target.checked)}
                      className="mr-2 accent-olive-600"
                    />
                    <span className={`text-sm ${theme.textPrimary}`}>{account}</span>
                  </label>
                ))}
                {accounts.length === 0 && (
                  <p className={`text-sm ${theme.textMuted}`}>No accounts linked</p>
                )}
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium ${theme.textSecondary} mb-2`}>Instruments</label>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {symbols.map(symbol => (
                  <label key={symbol} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.instruments.includes(symbol)}
                      onChange={(e) => handleMultiSelectChange('instruments', symbol, e.target.checked)}
                      className="mr-2 accent-olive-600"
                    />
                    <span className={`text-sm ${theme.textPrimary}`}>{symbol}</span>
                  </label>
                ))}
                {symbols.length === 0 && (
                  <p className={`text-sm ${theme.textMuted}`}>No instruments configured</p>
                )}
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium ${theme.textSecondary} mb-2`}>Timeframes</label>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {staticFilterOptions.timeframes.map(timeframe => (
                  <label key={timeframe} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.timeframes.includes(timeframe)}
                      onChange={(e) => handleMultiSelectChange('timeframes', timeframe, e.target.checked)}
                      className="mr-2 accent-olive-600"
                    />
                    <span className={`text-sm ${theme.textPrimary}`}>{timeframe}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium ${theme.textSecondary} mb-2`}>
                Time Window (Local: {getUserTimezone()})
              </label>
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-1 mb-2">
                  {[{ label: 'Last Hour', hours: 1 }, { label: 'Last 24h', hours: 24 }, { label: 'Last Week', hours: 168 }, { label: 'Last 30d', hours: 720 }].map(({ label, hours }) => (
                    <button
                      key={hours}
                      type="button"
                      onClick={() => setTimeRange(hours)}
                      className={`px-2 py-1 text-xs rounded-lg ${theme.btnSecondary}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <input
                  type="datetime-local"
                  value={filters.time_from}
                  onChange={(e) => handleFilterChange('time_from', e.target.value)}
                  className={`w-full px-3 py-1 text-sm ${theme.input}`}
                  placeholder="From"
                />
                <input
                  type="datetime-local"
                  value={filters.time_to}
                  onChange={(e) => handleFilterChange('time_to', e.target.value)}
                  className={`w-full px-3 py-1 text-sm ${theme.input}`}
                  placeholder="To"
                />
              </div>
            </div>
          </div>

          <div className="mt-4 flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <label className={`flex items-center text-sm ${theme.textPrimary}`}>
                <span className="mr-2">Limit:</span>
                <select
                  value={filters.size}
                  onChange={(e) => handleFilterChange('size', parseInt(e.target.value))}
                  className={`px-2 py-1 text-sm ${theme.input}`}
                >
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                  <option value={500}>500</option>
                  <option value={1000}>1000</option>
                </select>
              </label>

              <label className={`flex items-center text-sm ${theme.textPrimary}`}>
                <span className="mr-2">Sort:</span>
                <select
                  value={filters.sort_order}
                  onChange={(e) => handleFilterChange('sort_order', e.target.value)}
                  className={`px-2 py-1 text-sm ${theme.input}`}
                >
                  <option value="desc">Newest First</option>
                  <option value="asc">Oldest First</option>
                </select>
              </label>
            </div>

            <div className="space-x-2">
              <button
                onClick={clearFilters}
                className={`px-3 py-1 rounded-lg text-sm font-semibold ${theme.btnSecondary}`}
              >
                Clear All
              </button>
              <button
                onClick={handleApplyFilters}
                className={`px-3 py-1 rounded-lg text-sm font-semibold ${theme.btnPrimary}`}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        {loading ? (
          <div className="text-center py-8">
            <div className={`inline-block animate-spin rounded-full h-8 w-8 border-2 ${theme.spinner}`}></div>
            <p className={`mt-2 ${theme.textSecondary}`}>Loading logs...</p>
          </div>
        ) : logs.length === 0 ? (
          <div className={`text-center py-8 ${theme.textMuted}`}>
            <p>No logs found with the current filters.</p>
            <p className="text-sm mt-2">Try adjusting your filters or time range.</p>
          </div>
        ) : (
          <table className={`min-w-full divide-y ${theme.tableBorder}`}>
            <thead className={theme.tableHeader}>
              <tr>
                {['Timestamp (Local)', 'Level', 'Broker', 'Account', 'Symbol', 'Timeframe', 'Message'].map(h => (
                  <th key={h} className={`px-6 py-3 text-left text-xs font-semibold ${theme.tableHeadText} uppercase tracking-wider`}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className={`bg-white divide-y ${theme.tableBorder}`}>
              {logs.map((log, index) => (
                <tr key={log.id || index} className={theme.tableRowHover}>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${theme.textMuted}`}>
                    {formatTimestamp(log.timestamp)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getLevelBadgeClass(log.level)}`}>
                      {log.level}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {log.broker && (
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getBrokerBadgeClass(log.broker)}`}>
                        {log.broker.toUpperCase()}
                      </span>
                    )}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${theme.textPrimary}`}>
                    {log.account || '-'}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${theme.textPrimary}`}>
                    {log.symbol || '-'}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${theme.textPrimary}`}>
                    {log.timeframe || '-'}
                  </td>
                  <td className={`px-6 py-4 text-sm ${theme.textPrimary} max-w-md`}>
                    <div className="break-words">
                      {log.level === 'snapshot' ? (
                        <pre className={`whitespace-pre-wrap font-mono text-xs ${theme.sectionBg} p-2 rounded-lg border ${theme.tableBorder} overflow-x-auto`}>
                          {log.message}
                        </pre>
                      ) : (
                        log.message
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {!loading && logs.length > 0 && (
        <div className={`mt-4 text-sm ${theme.textSecondary}`}>
          Showing {logs.length} log entries
        </div>
      )}
    </div>
  );
};

export default LogsViewer;
